import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * SF-182 Component 5: shared scroll-cue state for a horizontally-scrollable table/row, reusing
 * `RacesSection.tsx`'s existing scroll-listener + `canScrollLeft`/`canScrollRight` pattern (not
 * a static CSS `mask-image` — that never turns off and has a known history of breaking
 * `position: sticky` descendants under WebKit compositing, both real risks for a sticky-first-
 * column table). `RacesSection.tsx` itself is left untouched; this hook is for new call sites.
 *
 * Deliberate divergence from `RacesSection.tsx`: `canScrollRight` starts `false`, not `true`.
 * `RacesSection`'s row is guaranteed to overflow (fixed-width cards, always more than fit), so
 * an optimistic `true` is safe there; this hook's callers (data tables) may or may not overflow
 * depending on data, so starting `false` avoids a false fade cue on a table that turns out not to
 * need one.
 */
export function useHorizontalScrollAffordance<T extends HTMLElement>() {
  const scrollRef = useRef<T>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  return { scrollRef, canScrollLeft, canScrollRight };
}
